import React, { useState, useRef, useCallback } from 'react';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import TableChartIcon from '@mui/icons-material/TableChart';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import toast from 'react-hot-toast';

const STEPS = [
  { id: 1, label: 'Name' },
  { id: 2, label: 'Data' },
];

function parseCSV(text) {
  const lines = text.split('\n').filter(l => l.trim());
  if (lines.length < 2) return null;
  // Detect delimiter: tab, comma, semicolon
  const firstLine = lines[0];
  const delim = firstLine.includes('\t') ? '\t' : firstLine.includes(';') ? ';' : ',';
  return lines.map(line => {
    const result = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        inQuotes = !inQuotes;
      } else if (ch === delim && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += ch;
      }
    }
    result.push(current.trim());
    return result;
  });
}

const AnalystNewSession = ({ onComplete, onCancel }) => {
  const [step, setStep] = useState(1);
  const [sessionName, setSessionName] = useState('');
  const [dataSource, setDataSource] = useState(null); // 'upload' | 'sheets'
  const [uploadedData, setUploadedData] = useState(null);
  const [uploadFileName, setUploadFileName] = useState('');
  const fileInputRef = useRef(null);
  const nameInputRef = useRef(null);

  const canProceedStep1 = sessionName.trim().length > 0;
  const canFinish = dataSource === 'upload' && uploadedData !== null;

  const handleNext = () => {
    if (step === 1 && canProceedStep1) setStep(2);
  };

  const handleBack = () => {
    if (step === 2) setStep(1);
  };

  const handleFileUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const name = file.name.toLowerCase();
    if (!name.endsWith('.csv') && !name.endsWith('.tsv') && !name.endsWith('.txt')) {
      toast.error('Please upload a CSV, TSV, or TXT file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target.result;
      const parsed = parseCSV(text);
      if (!parsed || parsed.length < 2) {
        toast.error('File must have at least a header row and one data row');
        return;
      }
      setUploadedData(parsed);
      setUploadFileName(file.name);
    };
    reader.readAsText(file);
  }, []);

  const handleComplete = () => {
    let data2D = null;

    if (dataSource === 'upload') {
      data2D = uploadedData;
    }

    onComplete({
      name: sessionName.trim(),
      dataSource,
      data: data2D,
    });
  };

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">New Analyst Session</h2>
            <p className="text-xs text-gray-400 mt-0.5">Set up your workspace in two quick steps</p>
          </div>
          <button
            onClick={onCancel}
            className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
          >
            Cancel
          </button>
        </div>

        {/* Stepper indicator */}
        <div className="flex items-center gap-3 mb-8">
          {STEPS.map((s, i) => (
            <React.Fragment key={s.id}>
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                  step > s.id
                    ? 'bg-green-100 text-green-600'
                    : step === s.id
                    ? 'bg-brand-pink text-white'
                    : 'bg-gray-100 text-gray-400'
                }`}>
                  {step > s.id ? <CheckCircleOutlineIcon style={{ fontSize: '18px' }} /> : s.id}
                </div>
                <span className={`text-sm font-medium ${
                  step === s.id ? 'text-gray-800' : step > s.id ? 'text-green-600' : 'text-gray-400'
                }`}>
                  {s.label}
                </span>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`flex-1 h-px ${step > s.id ? 'bg-green-300' : 'bg-gray-200'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step 1: Name */}
        {step === 1 && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-1">Session Name</h3>
            <p className="text-xs text-gray-400 mb-4">Give your analysis a descriptive name</p>
            <input
              ref={nameInputRef}
              value={sessionName}
              onChange={(e) => setSessionName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleNext(); }}
              placeholder="e.g. Q1 Revenue Analysis, Marketing Funnel, Cohort Study..."
              className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg outline-none focus:border-brand-pink focus:ring-2 focus:ring-brand-pink/20 transition-all"
              autoFocus
            />
            <div className="flex justify-end mt-6">
              <button
                onClick={handleNext}
                disabled={!canProceedStep1}
                className="flex items-center gap-1.5 px-4 py-2 text-sm bg-brand-pink text-white rounded-lg hover:bg-brand-pink/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Next
                <ArrowForwardIcon style={{ fontSize: '16px' }} />
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Data Source */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-1">Data Source</h3>
              <p className="text-xs text-gray-400 mb-4">Choose how to load data into your spreadsheet</p>

              {/* Source options */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
                {/* Paste — goes straight to spreadsheet */}
                <button
                  onClick={() => {
                    onComplete({ name: sessionName.trim(), dataSource: 'paste', data: null });
                  }}
                  className="relative flex flex-col items-center gap-2 p-4 rounded-lg border-2 border-gray-200 hover:border-brand-pink hover:bg-brand-pink/5 transition-all"
                >
                  <ContentPasteIcon style={{ fontSize: '24px' }} className="text-gray-400" />
                  <span className="text-sm font-medium text-gray-600">Paste Data</span>
                  <span className="text-[10px] text-gray-400">Opens spreadsheet to paste</span>
                </button>

                {/* Upload */}
                <button
                  onClick={() => setDataSource('upload')}
                  className={`relative flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                    dataSource === 'upload'
                      ? 'border-brand-pink bg-brand-pink/5'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <UploadFileIcon style={{ fontSize: '24px' }} className={dataSource === 'upload' ? 'text-brand-pink' : 'text-gray-400'} />
                  <span className={`text-sm font-medium ${dataSource === 'upload' ? 'text-brand-pink' : 'text-gray-600'}`}>Upload File</span>
                  <span className="text-[10px] text-gray-400">CSV, TSV, or TXT</span>
                </button>

                {/* Google Sheets */}
                <button
                  onClick={() => toast('Google Sheets integration coming soon!', { icon: '🔗' })}
                  className="relative flex flex-col items-center gap-2 p-4 rounded-lg border-2 border-dashed border-gray-200 hover:border-gray-300 opacity-60 cursor-not-allowed transition-all"
                >
                  <TableChartIcon style={{ fontSize: '24px' }} className="text-gray-400" />
                  <span className="text-sm font-medium text-gray-500">Google Sheets</span>
                  <span className="text-[10px] text-gray-400">Coming soon</span>
                  <span className="absolute top-2 right-2 text-[9px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full">Soon</span>
                </button>
              </div>

              {/* File upload area */}
              {dataSource === 'upload' && (
                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.tsv,.txt"
                    className="hidden"
                    onChange={handleFileUpload}
                  />
                  {!uploadedData ? (
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full h-32 border-2 border-dashed border-gray-200 rounded-lg hover:border-brand-pink/50 flex flex-col items-center justify-center gap-2 transition-colors"
                    >
                      <UploadFileIcon className="text-gray-300" style={{ fontSize: '32px' }} />
                      <span className="text-sm text-gray-400">Click to select a file</span>
                      <span className="text-[10px] text-gray-400">CSV, TSV, or TXT — max 10MB</span>
                    </button>
                  ) : (
                    <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <CheckCircleOutlineIcon className="text-green-500" style={{ fontSize: '20px' }} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-700 truncate">{uploadFileName}</p>
                        <p className="text-[10px] text-gray-400">
                          {uploadedData.length} rows × {uploadedData[0]?.length || 0} columns
                        </p>
                      </div>
                      <button
                        onClick={() => { setUploadedData(null); setUploadFileName(''); }}
                        className="text-xs text-gray-400 hover:text-red-500 transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between">
              <button
                onClick={handleBack}
                className="flex items-center gap-1.5 px-4 py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                <ArrowBackIcon style={{ fontSize: '16px' }} />
                Back
              </button>
              <button
                onClick={handleComplete}
                disabled={!canFinish}
                className="flex items-center gap-1.5 px-5 py-2 text-sm bg-brand-pink text-white rounded-lg hover:bg-brand-pink/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Create Session
                <ArrowForwardIcon style={{ fontSize: '16px' }} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalystNewSession;
